/** Membership/Credits tab and its private credit-balance satellites. */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Divider, Link as MuiLink, Paper, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  getCreditSummary,
  getMemberMembership,
  type CreditBalance,
  type CreditSummaryEntry,
} from '@/api/members';
import { useToast } from '@/components/useToast';
import { useResource } from '@/hooks/useResource';
import { Icon, inkAlpha, MONO, resolveTokens } from '@/theme';
import { buildMembersMaps } from '@/theme/maps/members';
import { MEMBERS_FORBIDDEN_MESSAGE, MEMBERS_LOAD_FAILED_MESSAGE } from './membersError';
import { formatCredits } from '@/utils/v2Format';
import { scrubBrand } from '@/utils/whitelabel';
import { BuyMembershipOrderDialog } from './BuyMembershipOrderDialog';
import { ManageCreditsDialog } from './ManageCreditsDialog';
import { SellCartDialog } from './SellCartDialog';
import { MembershipSummaryBar } from './MembershipSummaryBar';
import { ScreenError } from '@/components/ScreenError';
import { daysBetween, todayIn, zonedDateOf } from '@/components/list/listDate';
import { useCalendarZone } from '@/location/useCalendarZone';

export function LedgerTab({
  contactId,
  name,
  balances,
  tier,
  status,
  onReload,
  reloadToken,
  onAddCredits,
  canAdjustCredits = true,
  canManage = true,
  onBuyMembership,
  editOpen,
  onEditOpenChange,
}: {
  contactId: string;
  name: string;
  balances: CreditBalance[] | null;
  tier: string | null;
  status: string | null;
  onReload: () => void;
  reloadToken: number;
  onAddCredits: () => void;
  /** Adjust all credits (DNKAPI-0670): false for a STAFF session, which comps instead. */
  canAdjustCredits?: boolean;
  /**
   * Manage credits (reduce, expiry) and membership changes — OWNER/ADMIN on the server, so
   * false for a STAFF session, which still sees balances and sells (DNKADM-0375).
   */
  canManage?: boolean;
  onBuyMembership: () => void;
  editOpen: boolean;
  onEditOpenChange: (open: boolean) => void;
}) {
  const theme = useTheme();
  const t = resolveTokens(theme);
  const membership = useResource(useCallback(() => getMemberMembership(contactId), [contactId]));
  const showToast = useToast();
  const hasNamedBuckets = useMemo(
    () => (balances ?? []).some((b) => b.creditBucketId !== null),
    [balances],
  );

  const [summary, setSummary] = useState<CreditSummaryEntry[] | null>(null);
  const [manageTarget, setManageTarget] = useState<CreditWorkspaceTarget | null>(null);
  // The sell cart's two entry points (DNKADM-0241): the "Sell a package" card opens it on
  // the package catalog, a bucket's Sell button opens it on credits with that bucket seeded.
  // One piece of state so only one cart is ever open, and null means closed.
  const [sellCart, setSellCart] = useState<SellCartRequest | null>(null);
  // The plan the Buy pre-flight gated (DNKADM-0231) — handed to the order dialog so the
  // ordered plan is exactly the pre-flighted one (the dialog opens locked to it).
  const [boundBuyPlanId, setBoundBuyPlanId] = useState<string | null>(null);

  // DNKADM-0436 V-16: a failed summary load is its own state. It used to null the summary, which
  // rendered a 500 or a dropped connection as "no credits". `summaryRetry` re-runs the load.
  const [summaryError, setSummaryError] = useState<unknown>(null);
  const [summaryRetry, setSummaryRetry] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setSummaryError(null);
    getCreditSummary(contactId)
      .then((entries) => {
        if (!cancelled) setSummary(entries);
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setSummary(null);
          setSummaryError(error ?? new Error('Credit summary failed to load'));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [contactId, reloadToken, summaryRetry]);

  // Empty ledger: null (no ledger account) OR an empty array (known account, no buckets).
  const hasBuckets = balances !== null && balances.length > 0;

  const buckets = balances ?? [];
  const summaryByBucket = new Map((summary ?? []).map((entry) => [entry.bucketId, entry]));
  const currentMembership = membership.data;

  return (
    <>
      {/* The tab's two money surfaces sit side by side (DNKADM-0241): the membership
          summary on the left, the sell-a-package entry on the right, equal width at
          `md`+ and stacked full-width below. Each card keeps its OWN bottom margin, which
          is what spaces the pair into the Credits heading below (the home-location card
          that used to sit between them moved to the header — DNKADM-0247). */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        sx={{ gap: { xs: 0, md: 2 }, alignItems: { xs: 'stretch', md: 'flex-start' } }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <MembershipSummaryBar
            membership={currentMembership}
            tier={tier}
            status={status}
            membershipReady={membership.status === 'ready'}
            // DNKADM-0333: the resource's THIRD state, which this screen used to drop on the
            // floor. `status` was read in exactly one place, so a failed load rendered
            // identically to "this member has no membership" — an empty card, no message, no
            // retry, and every action gone. Keyed off `status`, not off `error`, so a stale
            // error from a previous attempt can never survive into a fresh load.
            loadError={membership.status === 'error' ? membership.error : null}
            onRetryLoad={membership.refetch}
            onBuy={(planId) => {
              setBoundBuyPlanId(planId);
              onBuyMembership();
            }}
            contactId={contactId}
            canManage={canManage}
            onSubscriptionChanged={() => {
              membership.refetch();
              onReload();
            }}
          />
        </Box>
        <SellPackageCard
          onSell={() => setSellCart({ initialFocus: 'packages', seedCreditBucketId: null })}
        />
      </Stack>

      <Stack direction="row" sx={{ alignItems: 'baseline', gap: 1.25, flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: 18, fontWeight: 600 }}>Credits</Typography>
        {hasNamedBuckets && canAdjustCredits && (
          <MuiLink
            href="#adjust-credits"
            underline="hover"
            onClick={(event) => {
              event.preventDefault();
              onAddCredits();
            }}
            sx={{ fontSize: 14, fontWeight: 500, cursor: 'pointer' }}
          >
            Adjust all credits
          </MuiLink>
        )}
      </Stack>

      {summaryError !== null && (
        <ScreenError
          error={summaryError}
          what="the credit summary"
          onRetry={() => setSummaryRetry((n) => n + 1)}
          forbiddenMessage={MEMBERS_FORBIDDEN_MESSAGE}
          retryMessage={MEMBERS_LOAD_FAILED_MESSAGE}
          preferServerMessage
          sx={{ mt: 1, mb: 1 }}
        />
      )}

      <BalanceCardsGrid
        buckets={buckets}
        summaryByBucket={summaryByBucket}
        {...(canManage ? { onManageCredits: setManageTarget } : {})}
        onSellCredits={(creditBucketId) =>
          setSellCart({ initialFocus: 'credits', seedCreditBucketId: creditBucketId })
        }
      />

      {!hasBuckets && (
        <Paper elevation={1} sx={{ p: '32px 24px', textAlign: 'center', mt: 2 }}>
          <Icon name="account_balance_wallet" sx={{ fontSize: 46, color: t.lineStrong }} />
          <Typography sx={{ fontSize: 16, fontWeight: 500, mt: 1 }}>
            No credit ledger for this member yet
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.75, maxWidth: 380, mx: 'auto' }}>
            Credit buckets appear here once a membership is assigned or you grant the first credits.
            Nothing has been recorded for {name}.
          </Typography>
        </Paper>
      )}

      {!hasNamedBuckets && canAdjustCredits && (
        <Typography variant="body2" sx={{ color: t.text2 }}>
          No credit buckets to adjust yet.
        </Typography>
      )}

      {/* The shared sell cart (DNKADM-0241) — one instance for both entry points. A
          completed sale grants credits server-side, so the balances + summary refetch
          exactly the way a credit adjust does. */}
      {sellCart !== null && (
        <SellCartDialog
          contactId={contactId}
          open
          initialFocus={sellCart.initialFocus}
          seedCreditBucketId={sellCart.seedCreditBucketId}
          onClose={() => setSellCart(null)}
          onPurchased={() => {
            membership.refetch();
            onReload();
          }}
        />
      )}

      {manageTarget !== null && (
        <ManageCreditsDialog
          contactId={contactId}
          bucketId={manageTarget.bucketId}
          bucketName={manageTarget.bucketName}
          onClose={() => setManageTarget(null)}
          onCreditsChanged={onReload}
        />
      )}

      {contactId && currentMembership && (
        <BuyMembershipOrderDialog
          contactId={contactId}
          open={editOpen}
          boundPlanId={boundBuyPlanId ?? undefined}
          onClose={() => {
            setBoundBuyPlanId(null);
            onEditOpenChange(false);
          }}
          onCompleted={(order) => {
            const cardOrder = order.payments.some((payment) => payment.mode === 'card');
            showToast(
              `${cardOrder ? 'Membership purchased.' : 'Membership comped.'} Order ${
                order.orderNumber ?? order.orderId ?? ''
              }`.trim(),
            );
            membership.refetch();
            onReload();
          }}
          onReversed={(order) => {
            const action = order.status === 'REFUNDED' ? 'refunded' : 'voided';
            showToast(
              `Membership order ${action}. Order ${order.orderNumber ?? order.orderId ?? ''}`.trim(),
            );
            membership.refetch();
            onReload();
          }}
        />
      )}
    </>
  );
}

/** How the sell cart was opened — which catalog it lands on, and the seeded bucket (if any). */
interface SellCartRequest {
  initialFocus: 'packages' | 'credits';
  /** The bucket the per-bucket Sell button seeded at qty 1; null from the package card. */
  seedCreditBucketId: string | null;
}

/**
 * The sell-cart entry point beside the membership summary bar (DNKADM-0241) — same card
 * treatment as the bar so the two read as a pair.
 */
function SellPackageCard({ onSell }: { onSell: () => void }) {
  const theme = useTheme();
  const t = resolveTokens(theme);
  const members = buildMembersMaps(theme.palette.mode === 'dark' ? 'dark' : 'light');
  return (
    <Box
      data-testid="sell-package-card"
      sx={{
        p: { xs: '16px', md: '16px 18px' },
        borderRadius: '12px',
        border: `1px solid ${t.line}`,
        bgcolor: t.surface,
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: { xs: 'stretch', md: 'center' },
        justifyContent: 'space-between',
        gap: 2,
        mb: 2,
      }}
    >
      <Stack direction="row" sx={{ alignItems: 'center', gap: 1.5, minWidth: 0, flex: 1 }}>
        <Box
          aria-hidden="true"
          sx={{
            width: 38,
            height: 38,
            borderRadius: '10px',
            bgcolor: members.ledger.summaryBg,
            color: t.brand,
            display: 'grid',
            placeItems: 'center',
            flexShrink: 0,
          }}
        >
          <Icon name="shopping_cart" />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 16, fontWeight: 600 }}>Sell a package</Typography>
          <Typography sx={{ fontSize: 13, color: inkAlpha(theme, 0.62), mt: 0.5 }}>
            Packages and credits in one cart — card, comp, or external payment.
          </Typography>
        </Box>
      </Stack>
      <Stack
        direction="row"
        sx={{ justifyContent: { xs: 'flex-start', md: 'flex-end' }, flexShrink: 0 }}
      >
        {/* The card's heading already says WHAT is sold, so the action stays a plain
            verb; the per-bucket Sell buttons name their bucket in an `aria-label`, so
            no two "Sell" controls ever share an accessible name. */}
        <Button size="small" startIcon={<Icon name="shopping_cart" />} onClick={onSell}>
          Sell
        </Button>
      </Stack>
    </Box>
  );
}

function BalanceCardsGrid({
  buckets,
  summaryByBucket,
  onManageCredits,
  onSellCredits,
}: {
  buckets: CreditBalance[];
  summaryByBucket: Map<string, CreditSummaryEntry>;
  /** Open Manage credits; absent for a session that may not (STAFF, DNKADM-0375). */
  onManageCredits?: (target: CreditWorkspaceTarget) => void;
  /** Open the sell cart seeded with this bucket (DNKADM-0241). */
  onSellCredits: (creditBucketId: string) => void;
}) {
  const theme = useTheme();
  const t = resolveTokens(theme);
  // The expiring/arriving day counts are on the location calendar (DNKADM-0439).
  const calendarZone = useCalendarZone();
  return (
    <Box
      data-testid="membership-credits-card-grid"
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
      }}
      sx={{
        gap: 1.5,
        mt: 1.25,
      }}
    >
      {buckets.map((bucket, index) => {
        const key = bucket.creditBucketId ?? bucket.bucketName ?? `bucket-${index}`;
        const summary =
          bucket.creditBucketId !== null ? summaryByBucket.get(bucket.creditBucketId) : undefined;
        const available = summary?.available ?? bucket.balance;
        const bucketName = bucket.bucketName ?? summary?.bucketName ?? 'Bucket';
        // The bucket name is BACKEND text, so it is scrubbed at this render boundary
        // (DNKADM-0245). The standing white-label guard scans source for brand
        // LITERALS and cannot see a brand that arrives at runtime inside an
        // interpolation — a bucket named with it would otherwise be read out verbatim
        // by a screen reader. ONE derived value feeds the heading and every label here
        // so the rendered name and its accessible name can never drift apart.
        const safeBucketName = scrubBrand(bucketName) ?? bucketName;
        const adjustable = bucket.creditBucketId !== null;
        const unavailableTitle = 'This credit bucket cannot be adjusted.';
        const manageLabel = adjustable
          ? `Manage credits for ${safeBucketName}`
          : `${safeBucketName} cannot be adjusted; Manage credits unavailable`;
        // Selling seeds the cart with THIS bucket, so it needs the same bucket id Manage
        // does — an unidentified bucket can be neither adjusted nor sold (DNKADM-0241).
        const unavailableSellTitle = 'Credits cannot be sold for this bucket.';
        const sellLabel = adjustable
          ? `Sell credits for ${safeBucketName}`
          : `${safeBucketName} cannot be sold; Sell credits unavailable`;
        return (
          <Box
            key={key}
            sx={{
              p: '14px 16px',
              borderRadius: '12px',
              border: `1px solid ${t.line}`,
              bgcolor: t.surface,
              minHeight: 168,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Typography sx={{ fontSize: 13, fontWeight: 500, color: inkAlpha(theme, 0.62) }}>
              {safeBucketName}
            </Typography>
            <Typography
              sx={{
                fontFamily: MONO,
                fontSize: 24,
                lineHeight: 1.25,
                fontWeight: 500,
                mt: 0.75,
              }}
            >
              {formatCredits(available)}
            </Typography>
            <Stack sx={{ gap: 0.35, mt: 1, flex: 1 }}>
              {summary && (
                <Typography sx={{ fontSize: 12, color: inkAlpha(theme, 0.58) }}>
                  {summary.expiringSoon
                    ? formatExpiringHint(summary.expiringSoon, calendarZone)
                    : 'No credits expiring.'}
                </Typography>
              )}
              {summary?.arrivingSoon && (
                <Typography sx={{ fontSize: 12, color: inkAlpha(theme, 0.58) }}>
                  {formatArrivingHint(summary.arrivingSoon, calendarZone)}
                </Typography>
              )}
            </Stack>
            <Divider sx={{ mt: 1.5 }} />
            <Stack direction="row" sx={{ mt: 1, gap: 0.75 }}>
              {onManageCredits !== undefined && (
                <Button
                  size="small"
                  startIcon={<Icon name="account_balance_wallet" />}
                  disabled={!adjustable}
                  title={adjustable ? undefined : unavailableTitle}
                  aria-label={manageLabel}
                  sx={{ flex: 1, minWidth: 0 }}
                  onClick={() => {
                    if (bucket.creditBucketId !== null) {
                      // The workspace only DISPLAYS this name, so it carries the scrubbed
                      // one — the same render-boundary rule, one hop further.
                      onManageCredits({ bucketId: bucket.creditBucketId, bucketName: safeBucketName });
                    }
                  }}
                >
                  Manage
                </Button>
              )}
              <Button
                size="small"
                startIcon={<Icon name="shopping_cart" />}
                disabled={!adjustable}
                title={adjustable ? undefined : unavailableSellTitle}
                aria-label={sellLabel}
                sx={{ flex: 1, minWidth: 0 }}
                onClick={() => {
                  if (bucket.creditBucketId !== null) onSellCredits(bucket.creditBucketId);
                }}
              >
                Sell
              </Button>
            </Stack>
          </Box>
        );
      })}
    </Box>
  );
}

interface CreditWorkspaceTarget {
  bucketId: string;
  bucketName: string;
}

function formatExpiringHint(hint: CreditSummaryEntry['expiringSoon'], zone: string | null): string {
  if (hint === null) return '';
  const days = futureDayCount(hint.date, zone);
  if (days <= 0) return `${formatCredits(hint.amount)} credits expire today`;
  return `${formatCredits(hint.amount)} credits expire in ${days} days`;
}

function formatArrivingHint(hint: CreditSummaryEntry['arrivingSoon'], zone: string | null): string {
  if (hint === null) return '';
  const days = futureDayCount(hint.date, zone);
  if (days <= 0) return `${formatCredits(hint.amount)} arriving today`;
  return `${formatCredits(hint.amount)} arriving in ${days} days`;
}

/**
 * Calendar days from today to a hint's day, both on the `zone` calendar (the location's,
 * DNKADM-0439); 0 when the day is today, past, or unreadable. A `YYYY-MM-DD` is the API's
 * calendar day, used as sent (never parsed at midnight); an instant is read on `zone`'s day.
 */
function futureDayCount(value: string, zone: string | null): number {
  let target: string | null = null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) target = value;
  else {
    const ms = Date.parse(value);
    if (Number.isFinite(ms)) target = zonedDateOf(ms, zone);
  }
  const days = target === null ? null : daysBetween(todayIn(zone), target);
  return days === null || days <= 0 ? 0 : days;
}

